import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { 
  Bookmark, BookOpen, CheckCircle, XCircle, ArrowRightLeft, 
  Heart, MessageCircle, TrendingUp, Timer, Star, FileText,
  Loader2, Filter
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

interface Activity {
  id: string;
  type: string;
  book_id: string | null;
  book_title: string | null;
  book_cover_url: string | null;
  payload: any;
  created_at: string;
  rating_value?: number;
  full_review_text?: string;
}

const activityIcons: Record<string, any> = {
  book_saved: Bookmark,
  book_started: BookOpen,
  book_finished: CheckCircle,
  book_dnf: XCircle,
  shelf_changed: ArrowRightLeft,
  review_liked: Heart,
  review_commented: MessageCircle,
  reading_progress_updated: TrendingUp,
  focus_session_started: Timer,
  focus_session_completed: Timer,
  rating_created: Star,
  rating_updated: Star,
  review_created: FileText,
  review_updated: FileText,
};

const activityLabels: Record<string, string> = {
  book_saved: 'Saved to TBR',
  book_started: 'Started Reading',
  book_finished: 'Finished',
  book_dnf: 'Did Not Finish',
  shelf_changed: 'Moved to Another Shelf',
  review_liked: 'Liked a Review',
  review_commented: 'Commented on Review',
  reading_progress_updated: 'Updated Progress',
  focus_session_started: 'Started Focus Session',
  focus_session_completed: 'Completed Focus Session',
  rating_created: 'Rated',
  rating_updated: 'Updated Rating',
  review_created: 'Wrote Review',
  review_updated: 'Updated Review',
};

interface UserActivityLogProps {
  userId: string;
}

export function UserActivityLog({ userId }: UserActivityLogProps) {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState<string>('all');
  const [total, setTotal] = useState(0);

  useEffect(() => {
    loadActivities();
  }, [userId, filterType]);

  const loadActivities = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('get-user-activity-log', {
        body: { 
          userId,
          limit: 50,
          offset: 0,
          activityType: filterType === 'all' ? undefined : filterType
        }
      });

      if (error) throw error;

      setActivities(data.activities || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Error loading activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActivityColor = (type: string) => {
    if (type.includes('finished') || type.includes('completed')) return 'text-success';
    if (type.includes('dnf') || type === 'book_dnf') return 'text-destructive';
    if (type.includes('started')) return 'text-primary';
    if (type.includes('liked')) return 'text-pink-500';
    return 'text-muted-foreground';
  };

  const renderActivityDetails = (activity: Activity) => {
    const { type, payload, rating_value, full_review_text } = activity;

    // Review activities - show full review text if available
    if ((type === 'review_created' || type === 'review_updated') && full_review_text) {
      return (
        <div className="mt-2 space-y-2">
          <div className="p-3 bg-muted/50 rounded-md">
            <p className="text-sm whitespace-pre-wrap">{full_review_text}</p>
          </div>
        </div>
      );
    }

    // Rating activities - show stars prominently, and review text if it exists
    if ((type === 'rating_created' || type === 'rating_updated') && rating_value) {
      const stars = '⭐'.repeat(Math.floor(rating_value));
      return (
        <div className="mt-2 space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-lg font-semibold text-primary">{stars}</span>
            <span className="text-sm font-medium">{rating_value}/5</span>
          </div>
          {full_review_text && (
            <div className="p-3 bg-muted/50 rounded-md">
              <p className="text-sm whitespace-pre-wrap">{full_review_text}</p>
            </div>
          )}
        </div>
      );
    }

    if (type === 'reading_progress_updated') {
      return (
        <span className="text-sm text-muted-foreground">
          Page {payload?.current_page || 0} of {payload?.total_pages || 0}
          {payload?.progress_percent && ` (${Math.round(payload.progress_percent)}%)`}
        </span>
      );
    }

    if (type === 'focus_session_completed') {
      return (
        <span className="text-sm text-muted-foreground">
          Completed {payload?.duration_minutes || 0} minute session
        </span>
      );
    }

    if (type === 'review_commented') {
      return (
        <span className="text-sm text-muted-foreground">
          {payload?.comment_excerpt}...
        </span>
      );
    }

    if (type === 'shelf_changed') {
      return (
        <span className="text-sm text-muted-foreground">
          From "{payload?.old_shelf}" to "{payload?.new_shelf}"
        </span>
      );
    }

    return null;
  };

  const uniqueActivityTypes = Array.from(
    new Set(activities.map(a => a.type))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4" />
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All Activities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Activities ({total})</SelectItem>
              {uniqueActivityTypes.map(type => (
                <SelectItem key={type} value={type}>
                  {activityLabels[type] || type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Badge variant="secondary">{activities.length} items</Badge>
      </div>

      <div className="space-y-2 max-h-[600px] overflow-y-auto">
        {activities.length === 0 ? (
          <Card className="p-8 text-center">
            <p className="text-muted-foreground">No activities found</p>
          </Card>
        ) : (
          activities.map((activity) => {
            const Icon = activityIcons[activity.type] || BookOpen;
            
            return (
              <Card key={activity.id} className="p-4">
                <div className="flex gap-4">
                  {activity.book_cover_url && (
                    <img
                      src={activity.book_cover_url}
                      alt={activity.book_title || 'Book'}
                      className="w-12 h-16 object-cover rounded"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <Icon className={`w-4 h-4 ${getActivityColor(activity.type)}`} />
                        <span className="font-medium">
                          {activityLabels[activity.type] || activity.type}
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
                      </span>
                    </div>
                    {activity.book_title && (
                      <p className="text-sm text-muted-foreground mt-1 truncate">
                        {activity.book_title}
                      </p>
                    )}
                    {renderActivityDetails(activity)}
                  </div>
                </div>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}
